/**
 * Tag Scroll Component
 *
 * Adds left/right navigation buttons and drag-to-scroll to tag strip containers.
 * Buttons appear only when there is more content to scroll in that direction.
 * Clicking a button scrolls to the next/previous partially hidden element.
 * The track is also horizontally draggable with the mouse.
 *
 * @package oska
 */

/**
 * TagScroll class
 * @param {HTMLElement} element - Container with [data-tag-scroll]
 */
class TagScroll {
  constructor(element) {
    this.container = element;
    this.track = element.querySelector("[data-tag-scroll-track]");
    this.leftOverlay = element.querySelector("[data-tag-scroll-left]");
    this.rightOverlay = element.querySelector("[data-tag-scroll-right]");
    this.prevBtn = element.querySelector("[data-tag-scroll-prev]");
    this.nextBtn = element.querySelector("[data-tag-scroll-next]");

    if (!this.track) return;

    this.isDragging = false;
    this.dragStartX = 0;
    this.dragScrollLeft = 0;
    this.dragMoved = false;

    this.init();
  }

  init() {
    this.updateButtons();
    this.bindEvents();

    this.resizeObserver = new ResizeObserver(() => this.updateButtons());
    this.resizeObserver.observe(this.track);
  }

  updateButtons() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const canScrollLeft = scrollLeft > 1;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

    if (this.leftOverlay) this.leftOverlay.hidden = !canScrollLeft;
    if (this.rightOverlay) this.rightOverlay.hidden = !canScrollRight;
  }

  scrollNext() {
    const trackRect = this.track.getBoundingClientRect();
    for (const child of this.track.children) {
      const rect = child.getBoundingClientRect();
      if (rect.right > trackRect.right - 2) {
        this.track.scrollTo({
          left: this.track.scrollLeft + rect.left - trackRect.left,
          behavior: "smooth",
        });
        return;
      }
    }
  }

  scrollPrev() {
    const trackRect = this.track.getBoundingClientRect();
    const children = Array.from(this.track.children);
    for (let i = children.length - 1; i >= 0; i--) {
      const rect = children[i].getBoundingClientRect();
      if (rect.left < trackRect.left + 2) {
        this.track.scrollTo({
          left: this.track.scrollLeft + rect.right - trackRect.right,
          behavior: "smooth",
        });
        return;
      }
    }
  }

  bindEvents() {
    this.track.addEventListener("scroll", () => this.updateButtons(), {
      passive: true,
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.scrollPrev();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.scrollNext();
      });
    }

    // Drag-to-scroll (mouse)
    this.track.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      this.isDragging = true;
      this.dragMoved = false;
      this.dragStartX = e.pageX - this.track.getBoundingClientRect().left;
      this.dragScrollLeft = this.track.scrollLeft;
      this.track.style.cursor = "grabbing";
      this.track.style.userSelect = "none";
    });

    this._onMouseUp = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.style.cursor = "";
      this.track.style.userSelect = "";
    };

    this._onMouseMove = (e) => {
      if (!this.isDragging) return;
      const x = e.pageX - this.track.getBoundingClientRect().left;
      const walk = x - this.dragStartX;
      if (Math.abs(walk) > 3) this.dragMoved = true;
      this.track.scrollLeft = this.dragScrollLeft - walk;
    };

    document.addEventListener("mouseup", this._onMouseUp);
    document.addEventListener("mousemove", this._onMouseMove);
  }

  destroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    document.removeEventListener("mouseup", this._onMouseUp);
    document.removeEventListener("mousemove", this._onMouseMove);
  }
}

const initialized = new WeakSet();

export default function initTagScroll() {
  // Init elements already in the DOM
  document.querySelectorAll("[data-tag-scroll]").forEach((el) => {
    if (!initialized.has(el)) {
      new TagScroll(el);
      initialized.add(el);
    }
  });

  // Watch for dynamically added elements (AJAX-loaded drawer items)
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const candidates = node.matches("[data-tag-scroll]")
          ? [node, ...node.querySelectorAll("[data-tag-scroll]")]
          : node.querySelectorAll("[data-tag-scroll]");
        candidates.forEach((el) => {
          if (!initialized.has(el)) {
            new TagScroll(el);
            initialized.add(el);
          }
        });
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}
